import { ChangeEvent, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import {
  Pager,
  Section,
  TableScroller,
  ToolbarInput,
  useBearer,
  useDebouncedValue,
  usePagedQuery,
} from './_admin-utils'

interface Nominee {
  nominee_email: string
  teacher_name?: string
  school?: string
  is_confirmed?: boolean
  confirmed_at?: string
  nomination_count?: number
}

interface Nominator {
  nominator_email?: string
  nominator_name?: string
  reason?: string
  created_at?: string
}

interface NomineeDetail {
  email: string
  teacher?: string
  school?: string
  is_confirmed?: boolean
  confirmed_at?: string
  items: Nominator[]
}

interface FetcherParams {
  page: number
  pageSize: number
}

export default function Nominations() {
  const { profile } = useAuth()
  const bearer = useBearer()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim(), 300)
  const [detail, setDetail] = useState<NomineeDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const fetcher = async ({ page, pageSize }: FetcherParams) => {
    if (!bearer) throw new Error('Not authenticated')
    const params = new URLSearchParams()
    if (debouncedQuery) params.set('q', debouncedQuery)
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))

    const response = await fetch(`/api/v1/admin/nominees?${params.toString()}`, {
      headers: { ...bearer },
    })
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(json?.error || 'Failed to load nominations')
    }
    return {
      items: Array.isArray(json?.data?.items) ? json.data.items : Array.isArray(json?.items) ? json.items : [],
      total: typeof json?.data?.total === 'number' ? json.data.total : typeof json?.total === 'number' ? json.total : 0,
    }
  }

  const { items, page, pageSize, total, pageCount, loading, error, load } = usePagedQuery(fetcher, [debouncedQuery])

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '')
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const handlePageChange = (nextPage: number) => {
    if (loading || nextPage === page) return
    load(nextPage)
  }

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = Number.parseInt(event.target.value, 10)
    const value = Number.isFinite(next) ? next : pageSize
    load(1, value)
  }

  const openDetail = async (nominee: Nominee) => {
    if (!bearer || !nominee?.nominee_email) return
    const baseDetail = {
      email: nominee.nominee_email,
      teacher: nominee.teacher_name,
      school: nominee.school,
      is_confirmed: nominee.is_confirmed,
      confirmed_at: nominee.confirmed_at,
      items: [],
    }
    setDetail(baseDetail)
    setDetailError(null)
    setLoadingDetail(true)
    try {
      const params = new URLSearchParams({ email: nominee.nominee_email })
      const response = await fetch(`/api/v1/admin/nominators?${params.toString()}`, {
        headers: { ...bearer },
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(json?.error || 'Failed to load nominators')
      }
      setDetail({ ...baseDetail, items: Array.isArray(json?.data?.items) ? json.data.items : Array.isArray(json?.items) ? json.items : [] })
    } catch (err: unknown) {
      setDetailError((err as Error | null)?.message || 'Failed to load nominators')
    } finally {
      setLoadingDetail(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Teacher Nominations</h1>
        <p className="text-sm text-cocoa/70">
          See which teachers are being nominated and drill into the nominators behind each submission.
        </p>
      </header>

      <Section
        title="Nominees"
        actions={
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <ToolbarInput value={query} onChange={setQuery} placeholder="Search by teacher or email" />
            <a href="/portal/admin" className="text-sm underline text-cocoa/70 hover:text-cocoa">
              ← Admin
            </a>
          </div>
        }
      >
        <div className="overflow-hidden rounded-xl border border-white/70">
          <TableScroller minWidth={720}>
            <table className="min-w-full divide-y divide-white/60 text-sm whitespace-nowrap">
              <thead className="sticky top-0 bg-shell/60 text-cocoa/70">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Teacher</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">School</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Nominations</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Status</th>
                  <th className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white/50">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-cocoa/60">
                    Loading nominees…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-cocoa/60">
                    {debouncedQuery ? 'No nominees match your search yet.' : 'No nominations yet.'}
                  </td>
                </tr>
              ) : (
                (items as Nominee[]).map((item) => (
                  <tr key={item.nominee_email} className="hover:bg-shell/40 transition-colors">
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-cocoa/90">{item.teacher_name || 'Unknown teacher'}</span>
                        <span className="font-mono text-xs text-cocoa/60">{item.nominee_email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-cocoa/80 align-top">{item.school || '—'}</td>
                    <td className="px-4 py-3 text-cocoa/80 align-top">{item.nomination_count}</td>
                    <td className="px-4 py-3 text-sm text-cocoa/80 align-top">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-medium ${
                            item.is_confirmed
                              ? 'bg-mint/30 text-mint'
                              : 'bg-terracotta/10 text-terracotta'
                          }`}
                        >
                          {item.is_confirmed ? 'Confirmed' : 'Pending confirmation'}
                        </span>
                        {item.confirmed_at ? (
                          <span className="text-xs text-cocoa/60">
                            Since {new Date(item.confirmed_at).toLocaleDateString()}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => openDetail(item)}
                        className="rounded-full border border-white/70 bg-white px-4 py-2 text-sm font-medium text-cocoa hover:bg-shell/60"
                      >
                        View supporters
                      </button>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </TableScroller>
        </div>

        {error ? <div className="text-sm text-red-600">Error: {String(error)}</div> : null}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm text-cocoa/70">
          <div>
            Showing page {page} of {pageCount} · {total} nominees
          </div>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
            <Pager page={page} pageCount={pageCount} onPage={handlePageChange} />
            <label className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-cocoa/60">Page size</span>
              <select
                className="rounded-lg border border-white/70 bg-shell/40 px-2 py-1 text-sm text-cocoa focus:border-terracotta focus:outline-none"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                {[10, 20, 50, 100].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {detail ? (
          <div className="rounded-xl border border-white/70 bg-white px-4 py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-cocoa">{detail.teacher || 'Teacher nomination'}</h3>
                <p className="font-mono text-xs text-cocoa/70">{detail.email}</p>
                {detail.school ? <p className="text-xs text-cocoa/60">{detail.school}</p> : null}
                <span
                  className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-medium ${
                    detail.is_confirmed ? 'bg-mint/30 text-mint' : 'bg-terracotta/10 text-terracotta'
                  }`}
                >
                  {detail.is_confirmed ? 'Confirmed' : 'Pending confirmation'}
                </span>
                {detail.confirmed_at ? (
                  <p className="text-xs text-cocoa/60">
                    Confirmed {new Date(detail.confirmed_at).toLocaleString()}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="text-xs font-medium text-cocoa/70 underline-offset-4 hover:underline"
                onClick={() => {
                  setDetail(null)
                  setDetailError(null)
                }}
              >
                Close
              </button>
            </div>
            {loadingDetail ? (
              <div className="mt-3 text-sm text-cocoa/70">Loading…</div>
            ) : detailError ? (
              <div className="mt-3 text-sm text-red-600">{detailError}</div>
            ) : detail.items.length === 0 ? (
              <div className="mt-3 text-sm text-cocoa/70">No supporters found yet.</div>
            ) : (
              <ul className="mt-3 space-y-3 text-sm text-cocoa/80">
                {detail.items.map((nominator, index) => (
                  <li key={`${nominator.nominator_email || 'anonymous'}-${index}`} className="flex flex-col gap-1">
                    <span className="font-semibold text-cocoa">
                      {nominator.nominator_name || 'Community supporter'}
                    </span>
                    <span className="font-mono text-xs text-cocoa/70">
                      {nominator.nominator_email || '—'}
                    </span>
                    {nominator.reason ? (
                      <span className="italic text-cocoa/80">“{nominator.reason}”</span>
                    ) : null}
                    <span className="text-xs text-cocoa/60">
                      {nominator.created_at ? new Date(nominator.created_at).toLocaleString() : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Section>
    </div>
  )
}
